"use client";
import { createLogAdapter } from "@/lib/logger/console-adapter";

import type { DelegationIdentity } from "@icp-sdk/core/identity";
// import { useSession, signOut } from "next-auth/react";
import {
	ArrowRightLeft,
	BarChart2,
	Bot,
	Calendar,
	Check,
	ChevronDown,
	Copy,
	Database,
	Eye,
	FileText,
	Frame,
	Gift,
	Globe,
	Home,
	LogOut,
	Map,
	MapPin,
	Menu,
	Rocket,
	Send,
	ShoppingCart,
	Star,
	TrendingUp,
	User,
	Wallet,
	Webcam,
	Wrench,
	X,
	Zap,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useRef, useState, type JSX } from "react";
import { AiNavIcon } from "@/components/ai/entry/ai-nav-icon";
import SignInModal from "@/components/auth/sign-in-modal";
import { DesktopHeaderSearch } from "@/components/search/desktop-header-search";
import { AccountAssetsModal } from "@/components/shared/AccountAssetsModal";
import { GlobalScanEntry } from "@/components/shared/global-scan-entry";
import { UserAccountPanel } from "@/components/shared/UserAccountPanel";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { VipLevelBadge } from "@/components/vip/VipGate";
import { useIdentity } from "@/contexts/IdentityContext";
import { useVip } from "@/contexts/VipContext";
import { useLanguage } from "@/hooks/useLanguage";
import { useNotifications } from "@/hooks/useNotifications";
import { getAiContextLabel } from "@/lib/ai/context";
import { writeAiEntryContext } from "@/lib/ai/client-settings";
import {
	discoverActivePatterns,
	discoverSections,
} from "@/lib/navigation/discover";
import { translations } from "@/lib/translations";
import { VipLevel } from "@/types/vip";
import { LanguageToggle } from "../providers/language-toggle";
import { VersionToggle } from "./VersionToggle";

import { ThemeToggle } from "../providers/theme-toggle";

const uiLog = createLogAdapter("components/layout/header.tsx");

interface HeaderProps {
	showLogo?: boolean;
}

type HeaderSession = {
	user?: {
		id?: string;
		email?: string;
		name?: string;
		image?: string;
	};
};

// 站点配置
const sites = [
	{
		id: "odin",
		name: "OdinFun",
		url: "https://odin.baby",
		description: "odin.baby",
		icon: "🌐",
	},
	{
		id: "fomowell",
		name: "FomoWell",
		url: "https://fomowell.baby",
		description: "fomowell.baby",
		icon: "🌐",
	},
];

// 站点切换器组件
const SiteSwitcherWithWebcam = () => {
	const [currentSite, setCurrentSite] = useState(sites[0]); // 默认显示 OdinFun

	const handleSiteChange = (site: (typeof sites)[0]) => {
		setCurrentSite(site); // 更新显示
		window.location.href = site.url; // 跳转到对应网址
	};

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button
					variant="ghost"
					size="sm"
					className="gap-2 border border-light-primary dark:border-dark-primary"
				>
					<Webcam className="w-4 h-4" />
					<span className="font-medium">{currentSite.name}</span>
					<ChevronDown className="w-3 h-3" />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="start" className="w-56">
				{sites.map((site) => (
					<DropdownMenuItem
						key={site.id}
						onClick={() => handleSiteChange(site)}
						className={site.id === currentSite.id ? "bg-accent" : ""}
					>
						<div className="flex items-center gap-3">
							<span className="text-lg">{site.icon}</span>
							<div className="flex-1">
								<div className="font-medium">{site.name}</div>
								<div className="text-xs text-muted-foreground">
									{site.description}
								</div>
							</div>
						</div>
					</DropdownMenuItem>
				))}
			</DropdownMenuContent>
		</DropdownMenu>
	);
};

export default function Header({ showLogo = false }: HeaderProps) {
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);
	const [isSearchExpanded, setIsSearchExpanded] = useState(false);
	const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
	const [isMoreMenuPinned, setIsMoreMenuPinned] = useState(false);
	const moreMenuRef = useRef<HTMLDivElement | null>(null);
	const moreMenuCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
		null,
	);
	const { language } = useLanguage();
	const t: any = translations[language] || translations.en;
	const pathname = usePathname();
	const router = useRouter();
	// const { data: session, status } = useSession();
	const session = null as HeaderSession | null;
	const status: "authenticated" | "unauthenticated" | "loading" =
		"unauthenticated";
	const [copiedEmail, setCopiedEmail] = useState(false);

	// 身份相关状态
	const {
		identity,
		isAuthenticated,
		principal,
		btcAddress,
		setAuthData,
		clearAuth,
	} = useIdentity();
	const { vipInfo, isVip } = useVip();
	const [showUserDropdown, setShowUserDropdown] = useState(false);
	const [isAssetsModalOpen, setIsAssetsModalOpen] = useState(false);
	const [delegationExpiration, setDelegationExpiration] = useState<
		bigint | null
	>(null);
	const {
		notifications,
		unreadCount,
		isLoading: isNotificationsLoading,
		markAsRead,
		markAllAsRead,
	} = useNotifications(isAuthenticated ? principal : undefined);
	const desktopSearchCollapsedWidth = isAuthenticated
		? "w-[clamp(12rem,18vw,16rem)]"
		: "w-[clamp(16rem,24vw,22rem)]";
	const desktopSearchExpandedWidth = isAuthenticated
		? "w-[clamp(22rem,34vw,30rem)]"
		: "w-[clamp(24rem,38vw,36rem)]";

	const formatEmail = (email?: string) => {
		if (!email) return "";
		const atIdx = email.indexOf("@");
		if (atIdx <= 3) return email; // 太短不省略
		return email.slice(0, 3) + "..." + email.slice(atIdx);
	};

	const handleCopyEmail = async () => {
		const email = session?.user?.email;
		if (!email) return;
		try {
			await navigator.clipboard.writeText(email);
			setCopiedEmail(true);
			setTimeout(() => setCopiedEmail(false), 2000);
		} catch (e) {
			// 可选：toast.error('复制失败');
		}
	};

	const handleSignOut = async () => {
		try {
			// Clear Telegram authentication data if user is logged in
			if (session?.user?.id) {
				await fetch("/api/auth/logout-telegram", {
					method: "POST",
					credentials: "include",
				});
			}

			// Clear browser storage including Telegram-specific data
			localStorage.clear();
			sessionStorage.clear();

			// Clear any Telegram-specific storage keys
			const telegramKeys = [
				"telegram_auth_data",
				"telegram_user",
				"telegram_session",
				"tg_user",
				"tg_auth",
				"telegram_login_data",
				"Telegram",
				"tgWebAppData",
				"tgWebAppVersion",
				"tgWebAppPlatform",
				"tgWebAppThemeParams",
			];

			telegramKeys.forEach((key) => {
				localStorage.removeItem(key);
				sessionStorage.removeItem(key);
			});

			// Clear Telegram widget global callback
			if ((window as any).TelegramLoginWidget) {
				delete (window as any).TelegramLoginWidget;
			}

			// Clear IndexedDB if it contains Telegram data
			if ("indexedDB" in window) {
				try {
					const databases = await indexedDB.databases();
					for (const db of databases) {
						if (db.name && db.name.toLowerCase().includes("telegram")) {
							await indexedDB.deleteDatabase(db.name);
						}
					}
				} catch (e) {}
			}

			// Clear Service Worker cache for Telegram
			if ("caches" in window) {
				try {
					const cacheNames = await caches.keys();
					for (const cacheName of cacheNames) {
						if (cacheName.toLowerCase().includes("telegram")) {
							await caches.delete(cacheName);
						}
					}
				} catch (e) {}
			}

			// Sign out with NextAuth
			// await signOut();
		} catch (error) {
			uiLog.error("Sign out error:", error);
			// Still proceed with sign out even if Telegram clearing fails
			// await signOut();
		}
	};

	// 处理钱包登录成功
	const handleLoginSuccess = (
		newPrincipal: string,
		address: string,
		newIdentity: DelegationIdentity,
	) => {
		setAuthData(newPrincipal, address, newIdentity);
		setIsSignInModalOpen(false);
	};

	// 处理退出登录
	const handleLogout = () => {
		clearAuth();
		setShowUserDropdown(false);
	};

	// 格式化 principal 显示（前5位...后3位）
	const formatPrincipal = (pid: string) => {
		if (pid.length <= 8) return pid;
		return `${pid.slice(0, 5)}...${pid.slice(-3)}`;
	};

	// 根据VIP等级获取颜色
	const getVipColors = () => {
		if (!isVip) {
			return {
				iconColor: "text-light-text dark:text-dark-text",
				hoverBg: "hover:bg-light-surface dark:hover:bg-dark-surface",
				badgeColor: "",
			};
		}

		switch (vipInfo.level) {
			case VipLevel.LEVEL_1:
				return {
					iconColor: "text-amber-500",
					hoverBg: "hover:bg-amber-50 dark:hover:bg-amber-900/20",
					badgeColor:
						"bg-gradient-to-r from-yellow-100 to-amber-100 text-amber-800 dark:from-yellow-900/30 dark:to-amber-900/30 dark:text-amber-300",
				};
			case VipLevel.LEVEL_2: // 黄金会员 - 金色
				return {
					iconColor: "text-yellow-500",
					hoverBg: "hover:bg-yellow-50 dark:hover:bg-yellow-900/20",
					badgeColor:
						"bg-gradient-to-r from-yellow-100 to-orange-100 text-orange-800 dark:from-yellow-900/30 dark:to-orange-900/30 dark:text-orange-400",
				};
			default:
				return {
					iconColor: "text-light-text dark:text-dark-text",
					hoverBg: "hover:bg-light-surface dark:hover:bg-dark-surface",
					badgeColor: "",
				};
		}
	};

	// 获取委托有效期
	useEffect(() => {
		if (isAuthenticated) {
			const storedDelegationResult = localStorage.getItem(
				"user_delegation_result",
			);
			if (storedDelegationResult) {
				try {
					const parsedChain = JSON.parse(
						storedDelegationResult,
						(key, value) => {
							if (key === "expiration" && typeof value === "string") {
								return BigInt(value);
							}
							return value;
						},
					);

					if (parsedChain?.delegationData?.delegation?.expiration) {
						setDelegationExpiration(
							BigInt(parsedChain.delegationData.delegation.expiration),
						);
					}
				} catch (error) {
					uiLog.error("Failed to parse delegation expiration:", error);
				}
			}
		}
	}, [isAuthenticated]);

	// 计算剩余有效期
	const getExpirationInfo = () => {
		if (!delegationExpiration) return null;

		const now = BigInt(Date.now()) * BigInt(1000000); // 转换为纳秒
		const remaining = delegationExpiration - now;

		if (remaining <= BigInt(0)) {
			return { expired: true, text: language === "zh" ? "已过期" : "Expired" };
		}

		const remainingMs = Number(remaining / BigInt(1000000)); // 转换为毫秒
		const hours = Math.floor(remainingMs / (1000 * 60 * 60));
		const minutes = Math.floor((remainingMs % (1000 * 60 * 60)) / (1000 * 60));

		let text;
		if (hours >= 24) {
			const days = Math.floor(hours / 24);
			const remainingHours = hours % 24;
			text =
				language === "zh"
					? `授权有效期剩余${days}天${remainingHours}小时`
					: `${days}d ${remainingHours}h left`;
		} else if (hours > 0) {
			text =
				language === "zh"
					? `授权有效期剩余${hours}小时${minutes}分钟`
					: `${hours}h ${minutes}m left`;
		} else {
			text =
				language === "zh"
					? `授权有效期剩余${minutes}分钟`
					: `${minutes} minutes left`;
		}

		// 格式化过期时间
		const expirationDate = new Date(
			Number(delegationExpiration / BigInt(1000000)),
		);
		const dateText =
			language === "zh"
				? `有效期至: ${expirationDate.toLocaleString("zh-CN")}`
				: `Valid until: ${expirationDate.toLocaleString()}`;

		return { expired: false, text, dateText, isLowTime: hours < 1 };
	};

	type NavigationLink = {
		name: string;
		href: string;
		icon: JSX.Element | ((active: boolean) => JSX.Element);
		activePatterns?: string[];
		isAi?: boolean;
		isNew?: boolean;
	};
	const navigationLinks: NavigationLink[] = [
		{
			name: t.nav?.plaza || "广场",
			href: "/plaza",
			icon: <MapPin className="w-4 h-4" />,
			activePatterns: ["/plaza"],
		},
		{
			name: "Agent",
			href: "/ai",
			icon: (active) => <AiNavIcon active={active} label="Agent" className="size-4" />,
			activePatterns: ["/ai"],
			isAi: true,
		},
		{
			name: t.nav?.data || "数据",
			href: "/data",
			icon: <Database className="w-4 h-4" />,
			activePatterns: ["/data"],
		},
		{
			name: t.nav?.scan || "监控",
			href: "/scan",
			icon: <Eye className="w-4 h-4" />,
			activePatterns: ["/scan"],
		},
	];

	const isNavigationLinkActive = (link: NavigationLink) => {
		const patterns = link.activePatterns ?? [link.href];
		return patterns.some((pattern) => {
			if (pattern === "/") {
				return pathname === "/";
			}
			return pathname === pattern || pathname.startsWith(`${pattern}/`);
		});
	};

	const isDiscoverActive = discoverActivePatterns.some(
		(pattern) => pathname === pattern || pathname.startsWith(`${pattern}/`),
	);

	const clearMoreMenuCloseTimer = () => {
		if (moreMenuCloseTimerRef.current) {
			clearTimeout(moreMenuCloseTimerRef.current);
			moreMenuCloseTimerRef.current = null;
		}
	};

	const openMoreMenu = () => {
		clearMoreMenuCloseTimer();
		setIsMoreMenuOpen(true);
	};

	const closeMoreMenuImmediately = () => {
		clearMoreMenuCloseTimer();
		setIsMoreMenuPinned(false);
		setIsMoreMenuOpen(false);
	};

	const closeMoreMenuWithDelay = () => {
		if (isMoreMenuPinned) {
			return;
		}
		clearMoreMenuCloseTimer();
		moreMenuCloseTimerRef.current = setTimeout(() => {
			setIsMoreMenuOpen(false);
			moreMenuCloseTimerRef.current = null;
		}, 220);
	};

	useEffect(() => {
		return () => {
			clearMoreMenuCloseTimer();
		};
	}, []);

	useEffect(() => {
		if (!isMoreMenuOpen) {
			return;
		}

		const handlePointerDownOutside = (event: PointerEvent) => {
			const target = event.target;
			if (!(target instanceof Node)) {
				return;
			}
			if (moreMenuRef.current?.contains(target)) {
				return;
			}
			closeMoreMenuImmediately();
		};

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				closeMoreMenuImmediately();
			}
		};

		document.addEventListener("pointerdown", handlePointerDownOutside);
		document.addEventListener("keydown", handleKeyDown);

		return () => {
			document.removeEventListener("pointerdown", handlePointerDownOutside);
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [isMoreMenuOpen]);

	return (
		<>
			<header className="sticky top-0 mb-2 z-layer-header w-full bg-light-background/80 dark:bg-dark-background/80 backdrop-blur-sm shadow-sm transition-all duration-200">
				<div className="mx-auto max-w-7xl px-3 sm:px-4 lg:px-5">
					<div className="flex items-center justify-between h-16">
						<div className="flex items-center gap-3">
							{/* Logo - Always visible */}
							<div className="flex items-center gap-2">
								<div className="relative w-8 h-8">
									<Image
										src="/images/baby-odin-logo.png"
										alt="BabyOdin"
										fill
										className="object-contain"
									/>
								</div>
							</div>

							{/* Site Switcher - 站点切换器紧跟在 logo 后面 */}
							<SiteSwitcherWithWebcam />
						</div>

						{!showLogo && (
							<div className="hidden min-w-0 flex-1 items-center gap-3 lg:flex">
								<div className="flex min-w-0 flex-1 items-center gap-1.5 transition-all duration-200">
									{navigationLinks.map((link) => {
										const isActive = isNavigationLinkActive(link);
										const linkClassName = `relative flex shrink-0 items-center gap-2 whitespace-nowrap rounded-lg px-2.5 py-2 transition-all duration-200 cursor-pointer ${
											isActive
												? "text-light-primary dark:text-dark-primary font-medium"
												: "text-light-text dark:text-dark-text hover:text-light-primary dark:hover:text-dark-primary"
										}`;
										const icon =
											typeof link.icon === "function" ? link.icon(isActive) : link.icon;

										if (link.isAi) {
											return (
												<button
													key={link.href}
													type="button"
													onClick={() => {
														writeAiEntryContext({
															source: "desktop-nav",
															pathname,
															label: getAiContextLabel(pathname),
															timestamp: Date.now(),
														});
														router.push("/ai?source=desktop-nav");
													}}
													className={`${linkClassName} cursor-pointer`}
													aria-label={language === "zh" ? "AI 分析顾问" : "AI Analyst"}
												>
													{icon}
													<span>{link.name}</span>
												</button>
											);
										}

										return (
											<Link key={link.href} href={link.href} className={linkClassName}>
												{icon}
												<span>{link.name}</span>
											</Link>
										);
									})}

									<div
										ref={moreMenuRef}
										className="relative shrink-0"
										onPointerEnter={openMoreMenu}
										onPointerLeave={closeMoreMenuWithDelay}
									>
										<button
											type="button"
											onClick={() => {
												if (isMoreMenuOpen && isMoreMenuPinned) {
													closeMoreMenuImmediately();
													return;
												}
												clearMoreMenuCloseTimer();
												setIsMoreMenuPinned(true);
												setIsMoreMenuOpen(true);
											}}
											className={`flex shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap rounded-lg px-2.5 py-2 outline-none transition-all duration-200 focus:outline-none focus-visible:outline-none focus-visible:ring-0 ${
												isDiscoverActive || isMoreMenuOpen
													? "text-light-primary dark:text-dark-primary font-medium"
													: "text-light-text dark:text-dark-text hover:text-light-primary dark:hover:text-dark-primary"
											}`}
											aria-expanded={isMoreMenuOpen}
											aria-haspopup="true"
										>
											<Wrench className="w-4 h-4" />
											<span>{language === "zh" ? "更多" : "More"}</span>
											<ChevronDown
												className={`w-3 h-3 transition-transform duration-200 ${
													isMoreMenuOpen ? "rotate-180" : ""
												}`}
											/>
										</button>
										<div
											className={`absolute left-0 top-full z-50 pt-2 ${
												isMoreMenuOpen ? "pointer-events-auto" : "pointer-events-none"
											}`}
											aria-hidden={!isMoreMenuOpen}
										>
											<div
												className={`w-[640px] rounded-2xl border border-black/10 bg-white/95 p-4 shadow-[0_18px_60px_rgba(15,23,42,0.16)] backdrop-blur transition-all duration-150 dark:border-white/10 dark:bg-[#17191d]/95 ${
													isMoreMenuOpen
														? "translate-y-0 opacity-100"
														: "translate-y-1 opacity-0"
												}`}
											>
												<div className="grid grid-cols-2 gap-4">
													{discoverSections.map((section) => (
														<div
															key={section.id}
															className="rounded-2xl border border-black/5 bg-black/[0.015] p-3 dark:border-white/10 dark:bg-white/[0.03]"
														>
																<div className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
																	{section.id === "activities" && language === "zh"
																		? "链游&活动"
																		: language === "zh"
																			? section.title
																			: section.titleEn}
																</div>
															<div className="space-y-1">
																{section.items.map((item) =>
																	item.external ? (
																		<a
																			key={item.id}
																			href={item.href}
																			target="_blank"
																			rel="noreferrer"
																			onClick={closeMoreMenuImmediately}
																			className="group flex cursor-pointer flex-col items-start rounded-xl px-3 py-3 transition-colors hover:text-light-primary dark:hover:text-dark-primary"
																		>
																			<span className="font-medium transition-colors group-hover:text-light-primary dark:group-hover:text-dark-primary">
																				{language === "zh" ? item.name : item.nameEn}
																			</span>
																			<span className="mt-0.5 text-xs text-muted-foreground transition-colors group-hover:text-light-primary/80 dark:group-hover:text-dark-primary/80">
																				{language === "zh"
																					? item.description
																					: item.descriptionEn}
																			</span>
																		</a>
																	) : (
																			<Link
																				key={item.id}
																				href={item.href}
																				onClick={closeMoreMenuImmediately}
																				className="group flex cursor-pointer flex-col items-start rounded-xl px-3 py-3 transition-colors hover:text-light-primary dark:hover:text-dark-primary"
																			>
																			<span className="font-medium transition-colors group-hover:text-light-primary dark:group-hover:text-dark-primary">
																				{language === "zh" ? item.name : item.nameEn}
																			</span>
																			<span className="mt-0.5 text-xs text-muted-foreground transition-colors group-hover:text-light-primary/80 dark:group-hover:text-dark-primary/80">
																				{language === "zh"
																					? item.description
																					: item.descriptionEn}
																			</span>
																		</Link>
																	),
																)}
															</div>
														</div>
													))}
												</div>
											</div>
										</div>
									</div>
								</div>

								<div
									className={`hidden xl:flex ${isSearchExpanded ? "min-w-0 flex-1" : "shrink-0"}`}
								>
									<div
										className={`transition-all duration-200 ${
											isSearchExpanded
												? desktopSearchExpandedWidth
												: desktopSearchCollapsedWidth
										}`}
									>
										<DesktopHeaderSearch onExpandedChange={setIsSearchExpanded} />
									</div>
								</div>
							</div>
						)}

						<div
							className={`flex shrink-0 items-center gap-1 transition-all duration-200 ${
								isSearchExpanded ? "opacity-0 pointer-events-none w-0 overflow-hidden" : ""
							}`}
						>
							<GlobalScanEntry
								buttonClassName="p-2 rounded-full text-light-text hover:text-light-text/80 hover:bg-light-surface dark:text-dark-text dark:hover:text-dark-text/80 dark:hover:bg-dark-surface transition-all duration-200 ease-in-out"
								iconClassName="h-5 w-5"
								ariaLabel={language === "zh" ? "打开扫一扫" : "Open scanner"}
								tooltipLabel={language === "zh" ? "扫一扫" : "Scan"}
							/>

							{/* Theme Toggle */}
							<ThemeToggle />

							{/* Language Toggle */}
							<div className="mr-1">
								<LanguageToggle />
							</div>

							{/* Version Toggle */}
							<div className="mr-4">
								<VersionToggle />
							</div>

							{/* 钱包登录功能 */}
							{isAuthenticated ? (
								<div className="relative">
									<TooltipProvider>
										<Tooltip>
											<TooltipTrigger asChild>
												<Button
													variant="ghost"
													size="sm"
													onClick={() => setShowUserDropdown(!showUserDropdown)}
													className={`relative flex max-w-[14rem] items-center gap-2 rounded-2xl px-3 py-2 transition-all duration-200 ${getVipColors().iconColor} ${getVipColors().hoverBg}`}
													aria-haspopup="menu"
													aria-expanded={showUserDropdown}
												>
													<User className="h-4 w-4 shrink-0" />
													<span className="truncate text-sm font-mono">
														{formatPrincipal(principal)}
													</span>
													{unreadCount > 0 && (
														<span className="absolute -right-1 -top-1 inline-flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold leading-5 text-white ring-2 ring-white dark:ring-dark-background">
															{unreadCount > 99 ? "99+" : unreadCount}
														</span>
													)}
													<ChevronDown className="h-3 w-3 shrink-0" />
												</Button>
											</TooltipTrigger>
											<TooltipContent>
												<p className="font-mono text-xs">{principal}</p>
												<p className="text-xs mt-1">
													{language === "zh" ? "查看账户与通知" : "Open account and notifications"}
												</p>
											</TooltipContent>
										</Tooltip>
									</TooltipProvider>

									{/* 用户下拉菜单 */}
									{showUserDropdown && (
										<div className="absolute right-0 top-full z-layer-panel mt-3 w-[400px] overflow-hidden rounded-[24px] border border-light-border bg-white shadow-[0_20px_80px_rgba(15,23,42,0.18)] dark:border-dark-border dark:bg-slate-950">
											<UserAccountPanel
												language={language}
												principal={principal}
												btcAddress={btcAddress}
												isVip={isVip}
												vipInfo={vipInfo}
												vipColors={getVipColors()}
												notifications={notifications}
												unreadCount={unreadCount}
												isNotificationsLoading={isNotificationsLoading}
												onMarkAllAsRead={markAllAsRead}
												onMarkAsRead={markAsRead}
												onLogout={handleLogout}
												onClose={() => setShowUserDropdown(false)}
												onOpenAssets={() => setIsAssetsModalOpen(true)}
												expirationInfo={getExpirationInfo()}
												notificationLimit={4}
												notificationViewportClassName="h-[208px]"
											/>
										</div>
									)}
								</div>
							) : (
								<Button
									onClick={() => setIsSignInModalOpen(true)}
									size="sm"
									className="bg-light-primary dark:bg-dark-primary hover:bg-light-primary/90 dark:hover:bg-dark-primary/90 text-white rounded-2xl px-4 py-2 shadow-md"
								>
									<Wallet className="h-4 w-4 mr-2" />
									{language === "zh" ? "连接钱包" : "Connect Wallet"}
								</Button>
							)}

							{/* Authentication - Hidden for now */}
							{false &&
								(status === "loading" ? (
									<div className="h-8 w-8 animate-pulse rounded-full bg-light-surface dark:bg-dark-surface" />
								) : session ? (
									<DropdownMenu>
										<DropdownMenuTrigger asChild>
											<Button
												variant="ghost"
												className="relative h-8 w-8 rounded-full"
											>
												<Avatar className="h-8 w-8">
														<AvatarImage
															src={session?.user?.image || ""}
															alt={session?.user?.name || ""}
														/>
													<AvatarFallback className="bg-light-surface dark:bg-dark-surface text-light-accent dark:text-dark-accent">
															{session?.user?.name?.charAt(0).toUpperCase() || "U"}
													</AvatarFallback>
												</Avatar>
											</Button>
										</DropdownMenuTrigger>
										<DropdownMenuContent
											className="w-56"
											align="end"
											forceMount
										>
											<DropdownMenuLabel className="font-normal">
												<div className="flex flex-col space-y-1">
													<p className="text-sm font-medium leading-none">
															{session?.user?.name}
													</p>
													<div className="flex items-center gap-1">
														<span className="text-xs leading-none text-muted-foreground">
																{formatEmail(session?.user?.email || undefined)}
														</span>
														<button
															onClick={handleCopyEmail}
															className="ml-1 p-0.5 rounded hover:bg-light-surface dark:hover:bg-dark-surface transition-colors"
															title="复制邮箱"
															type="button"
														>
															{copiedEmail ? (
																<Check className="w-3 h-3 text-status-success" />
															) : (
																<Copy className="w-3 h-3" />
															)}
														</button>
													</div>
												</div>
											</DropdownMenuLabel>
											<DropdownMenuSeparator />
											<DropdownMenuItem asChild>
												<Link href="/profile" className="flex items-center">
													<User className="mr-2 h-4 w-4" />
													<span>{t?.auth?.dashboard || "Dashboard"}</span>
												</Link>
											</DropdownMenuItem>
											<DropdownMenuSeparator />
											<DropdownMenuItem
												className="cursor-pointer"
												onClick={handleSignOut}
											>
												<LogOut className="mr-2 h-4 w-4" />
												<span>{t?.auth?.signOut || "Sign Out"}</span>
											</DropdownMenuItem>
										</DropdownMenuContent>
									</DropdownMenu>
								) : (
									<Button
										onClick={() => setIsSignInModalOpen(true)}
										size="sm"
										className="bg-light-primary hover:bg-light-primary/90 text-white dark:bg-dark-primary dark:hover:bg-dark-primary/90"
									>
										{t?.auth?.signIn || "Sign In"}
									</Button>
								))}
							<AccountAssetsModal
								open={isAssetsModalOpen}
								onOpenChange={setIsAssetsModalOpen}
								language={language}
								principal={principal}
							/>

							{/* Mobile menu button - Hidden as we use bottom navigation on mobile */}
							<button
								onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
								className="hidden p-2 ml-1 text-light-accent dark:text-dark-accent hover:bg-light-surface dark:hover:bg-dark-surface rounded-full transition-all duration-200"
								aria-label="Toggle mobile menu"
							>
								{isMobileMenuOpen ? (
									<X className="w-5 h-5" />
								) : (
									<Menu className="w-5 h-5" />
								)}
							</button>
						</div>
					</div>
				</div>
			</header>

			{isMobileMenuOpen && (
				<div
					className="md:hidden fixed inset-0 bg-black/50 z-40"
					onClick={() => setIsMobileMenuOpen(false)}
				/>
			)}

			<div
				className={`md:hidden fixed top-0 right-0 h-full w-64 bg-light-background dark:bg-dark-background shadow-lg z-50 transform transition-transform duration-300 ease-in-out ${
					isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
				}`}
			>
				<div className="p-4">
					<div className="flex justify-between items-center mb-6">
						<h2 className="text-lg font-semibold text-light-accent dark:text-dark-accent">
							Menu
						</h2>
						<button
							onClick={() => setIsMobileMenuOpen(false)}
							className="p-2 text-light-accent dark:text-dark-accent hover:bg-light-surface dark:hover:bg-dark-surface rounded-full"
						>
							<X className="w-5 h-5" />
						</button>
					</div>
						<nav className="flex flex-col gap-2">
							{navigationLinks.map((link) => {
								const isActive = isNavigationLinkActive(link);
								const linkClassName = `flex items-center gap-3 p-3 rounded-lg transition-all duration-200 relative ${
									isActive
										? "bg-light-primary/10 dark:bg-dark-primary/10 text-light-primary dark:text-dark-primary font-medium"
										: "text-light-text dark:text-dark-text hover:bg-light-surface dark:hover:bg-dark-surface"
								}`;
								const icon =
									typeof link.icon === "function" ? link.icon(isActive) : link.icon;

								if (link.isAi) {
									return (
										<button
											key={link.href}
											type="button"
											onClick={() => {
												writeAiEntryContext({
													source: "mobile-header-menu",
													pathname,
													label: getAiContextLabel(pathname),
													timestamp: Date.now(),
												});
												setIsMobileMenuOpen(false);
												router.push("/ai?source=mobile-header-menu");
											}}
											className={`${linkClassName} cursor-pointer`}
											aria-label={language === "zh" ? "AI 分析顾问" : "AI Analyst"}
										>
											{icon}
											<span>{link.name}</span>
										</button>
									);
								}

								return (
									<Link
										key={link.href}
										href={link.href}
										onClick={() => setIsMobileMenuOpen(false)}
										className={linkClassName}
									>
										{icon}
										<span>{link.name}</span>
										{link.isNew && (
											<span className="absolute -top-1 -right-1 bg-status-error text-white text-xs px-1.5 py-0.5 rounded-full font-bold animate-pulse">
												NEW
											</span>
										)}
									</Link>
								);
							})}

						{/* Activities Section for Mobile */}
						<div className="mt-2 mb-2">
							<div
								className={`flex items-center gap-3 p-3 relative ${
									pathname === "/activities" ||
									pathname.startsWith("/activities/") ||
									pathname === "/plaza/lucky-gift" ||
									pathname === "/plaza/lucky-wheel"
										? "text-light-primary dark:text-dark-primary"
										: "text-light-text/60 dark:text-dark-text/60"
								}`}
							>
								<Calendar className="w-4 h-4" />
								<span className="text-sm font-medium">
									{t.nav.activities || "活动"}
								</span>
								<span className="absolute top-1 right-1 bg-status-error text-white text-xs px-1.5 py-0.5 rounded-full font-bold animate-pulse">
									NEW
								</span>
							</div>
							<div className="ml-7 space-y-1">
								<Link
									href="/plaza/lucky-gift"
									onClick={() => setIsMobileMenuOpen(false)}
									className={`flex items-center gap-3 p-3 rounded-lg transition-all duration-200 ${
										pathname === "/plaza/lucky-gift"
											? "bg-light-primary/10 dark:bg-dark-primary/10 text-light-primary dark:text-dark-primary font-medium"
											: "text-light-text dark:text-dark-text hover:bg-light-surface dark:hover:bg-dark-surface"
									}`}
								>
									<Gift className="w-4 h-4" />
									<span>{t.nav.activitiesMenu?.luckyGift || "奥丁红包"}</span>
								</Link>
								<Link
									href="/plaza/lucky-wheel"
									onClick={() => setIsMobileMenuOpen(false)}
									className={`flex items-center gap-3 p-3 rounded-lg transition-all duration-200 ${
										pathname === "/plaza/lucky-wheel"
											? "bg-light-primary/10 dark:bg-dark-primary/10 text-light-primary dark:text-dark-primary font-medium"
											: "text-light-text dark:text-dark-text hover:bg-light-surface dark:hover:bg-dark-surface"
									}`}
								>
									<Rocket className="w-4 h-4" />
									<span>
										{t.nav.activitiesMenu?.luckyWheel || "奥丁宝符炉"}
									</span>
								</Link>
								<Link
									href="/activities"
									onClick={() => setIsMobileMenuOpen(false)}
									className={`flex items-center gap-3 p-3 rounded-lg transition-all duration-200 ${
										pathname === "/activities" ||
										pathname.startsWith("/activities/")
											? "bg-light-primary/10 dark:bg-dark-primary/10 text-light-primary dark:text-dark-primary font-medium"
											: "text-light-text dark:text-dark-text hover:bg-light-surface dark:hover:bg-dark-surface"
									}`}
								>
									<Calendar className="w-4 h-4" />
									<span>{t.nav.activitiesMenu?.airdrop || "空投"}</span>
								</Link>
							</div>
						</div>

						{/* Tools Section for Mobile */}
						<div className="mt-2 mb-2">
							<div className="flex items-center gap-3 p-3 text-light-text/60 dark:text-dark-text/60">
								<Wrench className="w-4 h-4" />
								<span className="text-sm font-medium">
									{t.nav.tools || "Tools"}
								</span>
							</div>
							<div className="ml-7 space-y-1">
								<Link
									href="/tools/frontrunner"
									onClick={() => setIsMobileMenuOpen(false)}
									className="flex items-center gap-3 p-3 text-light-text dark:text-dark-text hover:bg-light-surface dark:hover:bg-dark-surface rounded-lg transition-all duration-200"
								>
									<Bot className="w-4 h-4" />
									<span>{t.nav.toolsMenu?.frontrunner || "抢跑机器人"}</span>
								</Link>
								<Link
									href="/tools/batch-airdrop"
									onClick={() => setIsMobileMenuOpen(false)}
									className="flex items-center gap-3 p-3 text-light-text dark:text-dark-text hover:bg-light-surface dark:hover:bg-dark-surface rounded-lg transition-all duration-200"
								>
									<Gift className="w-4 h-4" />
									<span>{t.nav.toolsMenu?.batchAirdrop || "批量空投"}</span>
								</Link>
								<Link
									href="/tools/single-transfer"
									onClick={() => setIsMobileMenuOpen(false)}
									className="flex items-center gap-3 p-3 text-light-text dark:text-dark-text hover:bg-light-surface dark:hover:bg-dark-surface rounded-lg transition-all duration-200"
								>
									<Send className="w-4 h-4" />
									<span>{t.nav.toolsMenu?.singleTransfer || "单个转账"}</span>
								</Link>
								<a
									href="https://it.odin.baby"
									target="_blank"
									rel="noopener noreferrer"
									onClick={() => setIsMobileMenuOpen(false)}
									className="flex items-center gap-3 p-3 text-light-text dark:text-dark-text hover:bg-light-surface dark:hover:bg-dark-surface rounded-lg transition-all duration-200"
								>
									<ArrowRightLeft className="w-4 h-4" />
									<span>
										{t.nav.toolsMenu?.internalTransfer || "内部转账浏览器"}
									</span>
								</a>
								<Link
									href="/tools/holdings-monitor?tokenid=2rfi"
									onClick={() => setIsMobileMenuOpen(false)}
									className="flex items-center gap-3 p-3 text-light-text dark:text-dark-text hover:bg-light-surface dark:hover:bg-dark-surface rounded-lg transition-all duration-200"
								>
									<TrendingUp className="w-4 h-4" />
									<span>
										{t.nav.toolsMenu?.holdingsMonitor || language === "zh"
											? "持仓监控"
											: "Holdings Monitor"}
										</span>
									</Link>
								<Link
									href="/tools/rune-snapshot"
									onClick={() => setIsMobileMenuOpen(false)}
									className="flex items-center gap-3 p-3 text-light-text dark:text-dark-text hover:bg-light-surface dark:hover:bg-dark-surface rounded-lg transition-all duration-200"
								>
									<Database className="w-4 h-4" />
									<span>{language === "zh" ? "Rune 快照" : "Rune Snapshot"}</span>
								</Link>
								<Link
									href="/tools/rune-bridge"
									onClick={() => setIsMobileMenuOpen(false)}
									className="flex items-center gap-3 p-3 text-light-text dark:text-dark-text hover:bg-light-surface dark:hover:bg-dark-surface rounded-lg transition-all duration-200"
								>
									<Zap className="w-4 h-4" />
									<span>
										{language === "zh"
											? "符文桥"
											: t.nav.toolsMenu?.runeBridge || "Rune Bridge"}
									</span>
								</Link>
								<Link
									href="/tools/rune-address"
									onClick={() => setIsMobileMenuOpen(false)}
									className="flex items-center gap-3 p-3 text-light-text dark:text-dark-text hover:bg-light-surface dark:hover:bg-dark-surface rounded-lg transition-all duration-200"
								>
									<ArrowRightLeft className="w-4 h-4" />
									<span>
										{t.nav.toolsMenu?.runeAddress ||
											(language === "zh"
												? "Odin-Fomowell地址关联"
												: "Odin-Fomowell Address Links")}
									</span>
								</Link>
								<Link
									href="/tools/poster"
									onClick={() => setIsMobileMenuOpen(false)}
									className="flex items-center gap-3 p-3 text-light-text dark:text-dark-text hover:bg-light-surface dark:hover:bg-dark-surface rounded-lg transition-all duration-200"
								>
									<Frame className="w-4 h-4" />
									<span>{language === "zh" ? "海报" : "Poster"}</span>
								</Link>
								<a
									href="https://www.web3build.vip"
									target="_blank"
									rel="noopener noreferrer"
									onClick={() => setIsMobileMenuOpen(false)}
									className="flex items-center gap-3 p-3 text-light-text dark:text-dark-text hover:bg-light-surface dark:hover:bg-dark-surface rounded-lg transition-all duration-200"
								>
									<User className="w-4 h-4" />
									<span>
										{t.nav.toolsMenu?.web3BuildersAlliance ||
											"Web3建设者联盟"}
									</span>
								</a>
							</div>
						</div>

						{/* Authentication in Mobile Menu - Hidden for now */}
						{false && (
							<div className="border-t border-light-border dark:border-dark-border pt-3 mt-3">
								{status === "loading" ? (
									<div className="p-3">
										<div className="h-10 w-full animate-pulse rounded-lg bg-light-surface dark:bg-dark-surface" />
									</div>
								) : session ? (
									<div className="space-y-2">
										<div className="p-3 flex items-center gap-3">
											<Avatar className="h-10 w-10">
													<AvatarImage
														src={session?.user?.image || ""}
														alt={session?.user?.name || ""}
													/>
												<AvatarFallback className="bg-light-surface dark:bg-dark-surface text-light-accent dark:text-dark-accent">
														{session?.user?.name?.charAt(0).toUpperCase() || "U"}
												</AvatarFallback>
											</Avatar>
											<div className="flex-1 min-w-0">
												<p className="text-sm font-medium truncate">
													{session?.user?.name}
												</p>
												<p className="text-xs text-muted-foreground truncate flex items-center gap-1">
													{formatEmail(session?.user?.email || undefined)}
													<button
														onClick={handleCopyEmail}
														className="ml-1 p-0.5 rounded hover:bg-light-surface dark:hover:bg-dark-surface transition-colors"
														title="复制邮箱"
														type="button"
													>
														{copiedEmail ? (
															<Check className="w-3 h-3 text-status-success" />
														) : (
															<Copy className="w-3 h-3" />
														)}
													</button>
												</p>
											</div>
										</div>
										<Link
											href="/profile"
											onClick={() => setIsMobileMenuOpen(false)}
											className="flex items-center gap-3 p-3 text-light-accent dark:text-dark-accent hover:bg-light-surface dark:hover:bg-dark-surface rounded-lg transition-all duration-200"
										>
											<User className="w-5 h-5" />
											<span>{t?.auth?.dashboard || "Dashboard"}</span>
										</Link>
										<button
											onClick={async () => {
												await handleSignOut();
												setIsMobileMenuOpen(false);
											}}
											className="w-full flex items-center gap-3 p-3 text-light-text dark:text-dark-text hover:bg-light-surface dark:hover:bg-dark-surface rounded-lg transition-all duration-200"
										>
											<LogOut className="w-5 h-5" />
											<span>{t?.auth?.signOut || "Sign Out"}</span>
										</button>
									</div>
								) : (
									<div className="p-3">
										<Button
											onClick={() => {
												setIsSignInModalOpen(true);
												setIsMobileMenuOpen(false);
											}}
											className="w-full bg-light-primary hover:bg-light-primary/90 text-white dark:bg-dark-primary dark:hover:bg-dark-primary/90"
										>
											{t?.auth?.signIn || "Sign In"}
										</Button>
									</div>
								)}
							</div>
						)}
					</nav>
				</div>
			</div>

			{/* 钱包登录弹窗 */}
			<SignInModal
				open={isSignInModalOpen}
				onOpenChange={setIsSignInModalOpen}
				onSuccess={handleLoginSuccess}
			/>

			{/* 点击外部关闭下拉菜单 */}
			{showUserDropdown && (
				<div
					className="fixed inset-0 z-40"
					onClick={() => setShowUserDropdown(false)}
				/>
			)}
		</>
	);
}
